import { useEffect, useRef, useState } from "react";
import { Dialog, Text } from "@primer/react";
import { Button } from "../Button/Button.jsx";
import { MessageBox } from "../MessageBox/MessageBox.jsx";
import { SharedProfileStatus } from "../SharedProfile/SharedProfile.jsx";
import { createProfileShare } from "../../domain/profileShare.js";
import { loadSharedProfile, saveSharedProfile } from "../../lib/sharedProfileApi.js";
import {
  rememberSharedProfileLocalRevision, rememberSharedProfileSavedAt,
  sharedProfileLastSavedAt, sharedProfileLocalRevision
} from "../../storage/profileSharingPreferences.js";
import "./QuickSyncDialog.css";

const allProfileSections = { configuration: true, allocation: true, browserViews: true };

export function QuickSyncDialog({ application, fetchRequest = fetch, onClose, onSync, profile, returnFocusRef, token }) {
  const [server, setServer] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const initialFocusRef = useRef(null);
  const profileId = profile?.id;

  useEffect(() => {
    if (!profileId) return undefined;
    let active = true;
    loadSharedProfile(fetchRequest, token)
      .then((result) => { if (active) setServer(result); })
      .catch((error) => { if (active) setLoadError(error.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fetchRequest, profileId, token]);

  if (!profile) return null;
  const state = server ? syncState(profile, server) : null;

  async function push() {
    setWorking("Pushing…");
    setStatus(null);
    try {
      const saved = await saveSharedProfile(fetchRequest, token, createProfileShare(profile, allProfileSections));
      rememberSharedProfileSavedAt(profile.id, saved.updatedAt);
      rememberSharedProfileLocalRevision(profile.id, profile.updatedAt);
      setServer(saved);
      setConfirming(null);
      setStatus({ tone: "success", text: "Local data pushed to the server." });
      onSync?.();
    } catch (error) {
      setStatus({ tone: "danger", text: error.message });
    } finally {
      setWorking(null);
    }
  }

  async function pull() {
    setWorking("Pulling…");
    setStatus(null);
    try {
      const localRevision = new Date().toISOString();
      if (!await application.replaceProfileData(profile.id, server.share.profile.data, localRevision)) throw new Error("Local data could not be replaced.");
      rememberSharedProfileSavedAt(profile.id, server.updatedAt);
      rememberSharedProfileLocalRevision(profile.id, localRevision);
      setConfirming(null);
      setStatus({ tone: "success", text: "Server data pulled to this device." });
      onSync?.();
    } catch (error) {
      setStatus({ tone: "danger", text: error.message });
    } finally {
      setWorking(null);
    }
  }

  function choose(action) {
    if (requiresConfirmation(state, action)) setConfirming(action);
    else if (action === "pull") pull();
    else push();
  }

  const close = () => { if (!working) onClose(); };
  if (confirming) return <QuickSyncConfirmation action={confirming} onBack={() => setConfirming(null)} onClose={close} onConfirm={confirming === "pull" ? pull : push} profile={profile} returnFocusRef={returnFocusRef} working={working} />;

  return <Dialog className="quick-sync-dialog" title={`Sync ${profile.name}`} subtitle="Compare this device with its Server Sync copy." initialFocusRef={initialFocusRef} onClose={close} returnFocusRef={returnFocusRef}>
    <div className="quick-sync-body" ref={initialFocusRef} tabIndex={-1}>
      <SyncState error={loadError} loading={loading} state={state} />
    </div>
    <Dialog.Footer className="quick-sync-footer">
      <div className="quick-sync-status">{working ? <Text className="muted-text" role="status">{working}</Text> : <SharedProfileStatus status={status} />}</div>
      <div className="quick-sync-actions">
        <Button tone="secondary" onClick={close} disabled={Boolean(working)}>Close</Button>
        <Button tone={actionTone(state, "pull")} onClick={() => choose("pull")} disabled={!state || state.kind === "synced" || Boolean(working)}>Pull</Button>
        <Button tone={actionTone(state, "push")} onClick={() => choose("push")} disabled={!state || state.kind === "synced" || Boolean(working)}>Push</Button>
      </div>
    </Dialog.Footer>
  </Dialog>;
}

function SyncState({ error, loading, state }) {
  const display = displayedSyncState({ error, loading, state });
  return <>
    <MessageBox tone={display.tone} heading={display.heading}>{display.description}</MessageBox>
    <dl className="quick-sync-comparison">
      <div><dt>Local data</dt><dd>{display.local}</dd></div>
      <div><dt>Server data</dt><dd>{display.server}</dd></div>
    </dl>
  </>;
}

function QuickSyncConfirmation({ action, onBack, onClose, onConfirm, profile, returnFocusRef, working }) {
  const pulling = action === "pull";
  const initialFocusRef = useRef(null);
  return <Dialog className="quick-sync-dialog" title={pulling ? "Discard local changes?" : "Overwrite server changes?"} initialFocusRef={initialFocusRef} onClose={onClose} returnFocusRef={returnFocusRef}>
    <div className="quick-sync-body" ref={initialFocusRef} tabIndex={-1}><MessageBox tone="warning" heading="This cannot be undone">{pulling
      ? `Pulling will replace local data for ${profile.name} with the server data.`
      : `Pushing will replace the server data with local data from ${profile.name}.`}</MessageBox></div>
    <Dialog.Footer className="quick-sync-footer">
      <div className="quick-sync-status">{working ? <Text className="muted-text" role="status">{working}</Text> : null}</div>
      <div className="quick-sync-actions"><Button tone="secondary" onClick={onBack} disabled={Boolean(working)}>Back</Button><Button tone="danger" onClick={onConfirm} disabled={Boolean(working)}>{pulling ? "Pull and replace local data" : "Push and replace server data"}</Button></div>
    </Dialog.Footer>
  </Dialog>;
}

function displayedSyncState({ error, loading, state }) {
  if (loading) return {
    tone: "information", heading: "Checking status…", description: "Comparing local and server data.",
    local: "Checking…", server: "Checking…"
  };
  if (state) return { ...state, tone: state.kind === "synced" ? "information" : "warning" };
  return {
    tone: "danger", heading: "Status unavailable", description: error ?? "Server Sync could not be checked.",
    local: "Not checked", server: "Not checked"
  };
}

export function syncState(profile, server) {
  if (sameData(profile.data, server.share.profile.data)) return {
    kind: "synced", heading: "Up to date", description: "Local and server data are the same.",
    local: "Matches server", server: "Matches local"
  };
  const localRevision = sharedProfileLocalRevision(profile.id);
  const serverRevision = sharedProfileLastSavedAt(profile.id);
  const localChanged = !localRevision || profile.updatedAt !== localRevision;
  const serverChanged = !serverRevision || server.updatedAt !== serverRevision;
  if (localChanged && !serverChanged) return {
    kind: "local", heading: "Local changes available", description: "Push is recommended.",
    local: "Changes detected", server: "No changes"
  };
  if (!localChanged && serverChanged) return {
    kind: "server", heading: "Server changes available", description: "Pull is recommended.",
    local: "No changes", server: "Changes detected"
  };
  return {
    kind: "conflict", heading: "Changes on both sides", description: "Choose which version to keep.",
    local: "Changes detected", server: "Changes detected"
  };
}

function sameData(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function requiresConfirmation(state, action) {
  if (!state || state.kind === "synced") return false;
  return state.kind === "conflict" || (state.kind === "local" && action === "pull") || (state.kind === "server" && action === "push");
}

function actionTone(state, action) {
  if (!state || state.kind === "synced") return "secondary";
  if ((state.kind === "local" && action === "push") || (state.kind === "server" && action === "pull")) return "primary";
  return "danger";
}
